import React, { useCallback, useMemo, useState } from 'react'
import { Link, Outlet, useSearchParams } from 'react-router-dom'
import { DateRangePicker, Pill, Table } from '@demandlocal/components'
import { ReactComponent as GoogleIcon } from './../../../../../images/icons/audience_google.svg'
import { ReactComponent as FacebookIcon } from './../../../../../images/icons/audience_facebook.svg'
import { ReactComponent as IconChangeViewCard } from '../../../../../images/icons/icon_change_view_card.svg'
import { ReactComponent as IconChangeViewList } from '../../../../../images/icons/icon_change_view_list.svg'
import { ReactComponent as IconGoogleAds } from '../../../../../images/vendors/google_ads_logo.svg'
import { ReactComponent as IconFacebookAds } from '../../../../../images/vendors/meta_logo.svg'
import { ReactComponent as ArrowIcon } from '../../../../../images/icons/arrow_right.svg'
import { ReactComponent as IconPause } from './../../../../../images/icons/icon_pause.svg'
import { ReactComponent as IconPlay } from './../../../../../images/icons/icon_play.svg'
import { ReactComponent as CheckMarkIcon } from '../../../../../images/icons/green_check.svg'
import { DateRange } from '../../../../../lib/types/DateRange'
import { useCampaignsListView } from '../../../../../store/zustand/campaignsListStore'
import { useDateRange } from '../../../../../store/zustand/dateRangeStore'
import { createColumnHelper } from '@tanstack/react-table'
import { useGetSelfServiceCampaignsByDealershipId } from '../../../../../api/dealership/dsp/campaigns/useGetSelfServiceCampaignsByDealershipId'
import dayjs from 'dayjs'
import { match } from 'ts-pattern'
import { VENDOR } from '../../../../../lib/utils/vendors'
import { useEditVendorCampaignByDealershipId } from '../../../../../api/dealership/dsp/campaigns/useEditVendorCampaignByDealershipId'
import { toast } from 'react-hot-toast'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import Grid from '../../../../../components/Grid/Grid'
import CampaignOverviewCard from '../../../../../components/Campaigns/CampaignOverviewCard'
import { z } from 'zod'
import { SelfServiceCampaignSchema } from '../../../../../lib/schemas/campaignSchemas'
import { useTranslation } from 'react-i18next'
import { queryClient } from '../../../../../api/queryClient'
import LoadingAnimation from '../../../../../components/LoadingAnimation'
import { useGetLinkedAccounts } from '../../../../../api/agencies/useGetLinkedAccounts'
import classNames from 'classnames'
import { User } from '../../../../../lib/types/User'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../../../store/selectors/userSelector'

const columnHelper = createColumnHelper<z.infer<typeof SelfServiceCampaignSchema>>()

export default function CampaignsSelfServiceList(): JSX.Element {
  const dealership = useDealership()
  const user: User | null = useSelector(currentUserSelector)

  const { view, setView } = useCampaignsListView()
  const { mutateAsync: editCampaign } = useEditVendorCampaignByDealershipId()
  const { dateRange, setDateRange } = useDateRange()
  const [queryParams, setQueryParams] = useSearchParams()
  const { data, isLoading: isLoadingLinkedAccounts } = useGetLinkedAccounts()
  const oauthConnected = data?.find(
    (account) =>
      [VENDOR.Google, VENDOR.Facebook].includes(account.id) && account.connected,
  )

  const { t } = useTranslation('routes')
  const { data: selfServiceCampaigns, isLoading } =
    useGetSelfServiceCampaignsByDealershipId({
      pageIndex: 1,
      pageSize: 1,
    })

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })
  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const triggerCampaignState = useCallback(
    async (campaign) => {
      const toastId = toast.loading('Updating campaign status...')
      await editCampaign({
        data: {
          campaign_id: campaign.vendor_campaign.id,
          vendor_id: campaign.vendor_id,
          customer_account_id: campaign.vendor_campaign.account_id,
          login_customer_id: campaign.vendor_campaign.login_customer_id,
          status: campaign.vendor_campaign.status_name,
        },
      })
      await queryClient.invalidateQueries([
        'dealership',
        dealership?.id,
        'campaigns',
        'self-service',
        'list',
      ])
      toast.success('Campaign status updated', { id: toastId })
    },
    [dealership?.id, editCampaign],
  )

  const campaignColumns = useMemo(
    () => [
      columnHelper.accessor('vendor_campaign.status_name', {
        header: () => 'Campaign status',
        cell: (info) => {
          return (
            <Pill type={info.getValue() === 'active' ? 'success' : 'warning'}>
              {info.getValue()}
            </Pill>
          )
        },
      }),
      columnHelper.accessor('vendor_id', {
        header: () => 'Vendor',
        cell: (info) => {
          const vendor = info.getValue()
          return match(vendor)
            .with(VENDOR.Google, () => <GoogleIcon />)
            .with(VENDOR.Facebook, () => <FacebookIcon />)
            .otherwise(() => null)
        },
      }),
      columnHelper.accessor('vendor_campaign.start_date', {
        header: () => 'Booking Month',
        cell: (info) => dayjs(info.getValue()).format("MMM 'YY"),
      }),
      columnHelper.accessor('vendor_campaign.name', {
        header: () => 'Name',
        cell: (info) => {
          return (
            <Link
              className={'text-primary-persian-blue font-bold'}
              to={t('dealership.campaigns.self-service.report', {
                dealership,
                campaign: {
                  _id: info.row.original._id,
                },
              })}>
              [{info.row.original.vendor_campaign.account_name}] {info.getValue()}
            </Link>
          )
        },
      }),
      columnHelper.accessor('status', {
        header: () => 'Import status',
        cell: (info) => {
          const status = info.getValue()
          if (status === 'available') return <Pill type={'success'}>Live feed</Pill>
          else if (status === 'initial_load')
            return <Pill type={'warning'}>Data pull</Pill>
          else if (status === 'pending') return <Pill type={'warning'}>Importing</Pill>
          else return <Pill type={'error'}>Failed</Pill>
        },
      }),
      columnHelper.display({
        header: 'Impressions',
        id: 'impressions',
        cell: ({ row }) => {
          const impressions =
            row.original?.campaign_stats
              ?.map((stat) => stat.impressions)
              .reduce((a, b) => a + b, 0) ?? (0 as number)
          return new Intl.NumberFormat('en-US').format(impressions || 0)
        },
      }),
      columnHelper.display({
        header: 'Clicks',
        id: 'clicks',
        cell: ({ row }) => {
          const clicks =
            row.original?.campaign_stats
              ?.map((stat) => stat.clicks)
              .reduce((a, b) => a + b, 0) ?? (0 as number)
          return new Intl.NumberFormat('en-US').format(clicks || 0)
        },
      }),
      columnHelper.display({
        header: () => 'CTR',
        id: 'ctr',
        cell: ({ row }) => {
          const impressions =
            row.original?.campaign_stats
              ?.map((stat) => stat.impressions)
              .reduce((a, b) => a + b, 0) ?? (0 as number)
          const clicks =
            row.original?.campaign_stats
              ?.map((stat) => stat.clicks)
              .reduce((a, b) => a + b, 0) ?? (0 as number)
          return impressions ? `${((clicks / impressions) * 100).toFixed(2)}%` : 'N/A'
        },
      }),
      columnHelper.display({
        header: () => 'Actions',
        id: 'actions',
        cell: ({ row }) => {
          return (
            <div>
              <div
                onClick={(e) => {
                  triggerCampaignState(row.original)
                }}>
                {row.original.vendor_campaign.status_name === 'active' ? (
                  <IconPause className={'cursor-pointer'} />
                ) : (
                  <IconPlay className={'cursor-pointer'} />
                )}
              </div>
            </div>
          )
        },
      }),
    ],
    [dealership, t, triggerCampaignState],
  )

  if (isLoading || isLoadingLinkedAccounts) {
    return (
      <div className="my-auto mx-auto">
        <LoadingAnimation title="Loading..." />
      </div>
    )
  }

  if (Number(selfServiceCampaigns?.data?.length) === 0) {
    return (
      <div
        className={
          'flex flex-col  text-center my-auto mx-auto w-full w-2/3 items-center justify-center px-10'
        }>
        <span className={'font-bold text-3xl font-Spartan pb-1 pt-12'}>
          Import your self service campaigns
        </span>
        <span className={'text-base font-Spartan pt-1 pb-8'}>
          Here's how you can see your self service campaigns in a few easy steps!
        </span>
        <div className="flex flex-row gap-4 items-center justify-center">
          <div
            className={classNames('relative flex border-2 w-1/3 rounded-xl px-5 py-8', {
              'border-primary-persian-blue': !oauthConnected,
              'border-secondary-malachite-green bg-secondary-malachite-green/10 ':
                oauthConnected,
            })}>
            <div className="flex flex-col gap-3 text-center">
              {oauthConnected && (
                <div className="absolute top-0 right-0 flex items-center justify-end">
                  <div className="transform transform-translate translate-x-1/2 -translate-y-1/3">
                    <CheckMarkIcon />
                  </div>
                </div>
              )}
              <span className="text-sm text-web-gray font-bold">Step 1</span>
              <span className="font-bold text-md">Connect your account</span>
              {oauthConnected ? (
                <span className="text-sm">
                  You have successfully connected your account. You are now ready to
                  import your campaigns!
                </span>
              ) : Number(user?.role_id ?? 3) > 2 ? (
                <span className="text-sm">
                  Contact your agency administrator to OAuth their account in Agency {'>'}{' '}
                  Linked Platforms
                </span>
              ) : (
                <span className="text-sm">
                  Go to Linked Platforms to OAuth your account via{' '}
                  <Link
                    className="font-bold text-primary-persian-blue"
                    to={t('agency.profile.accounts', {
                      agency_id: dealership?.organization_id,
                    })}>
                    this link
                  </Link>
                </span>
              )}
              <div className="flex flex-row gap-3 items-center justify-center">
                <IconGoogleAds className={'w-8 h-8'} />
                <IconFacebookAds className={'w-8 h-8'} />
              </div>
            </div>
          </div>
          <ArrowIcon />
          <div
            className={classNames('border-2 w-1/3 rounded-xl px-5 py-8', {
              'opacity-50 bg-gainsboro-gray': !oauthConnected,
              'border-primary-persian-blue': oauthConnected,
            })}>
            <div className="flex flex-col gap-3 text-center">
              <span className="text-sm text-web-gray font-bold">Step 2</span>
              <span className="font-bold text-md">Import your campaigns</span>
              {!oauthConnected ? (
                <span className="text-sm">
                  You must connect your account before you're able to import your
                  campaigns
                </span>
              ) : (
                <span className="text-sm">
                  Choose the campaigns you want to import by clicking{' '}
                  <Link
                    className="font-bold text-primary-persian-blue"
                    to={'create/import'}>
                    this link
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>
        <Outlet />
      </div>
    )
  }

  return (
    <>
      <div className={'flex flex-col gap-12 pb-8'}>
        <div className={'flex flex-col gap-4'}>
          <span className="text-md pb-5">
            Monitor and manage your Facebook and Google campaigns connected through
            LinkOne. Used linked accounts under Agency to activate your Meta or Google
            accounts and see top level data.
          </span>
          <div className="flex flex-row justify-between">
            <span className={'font-bold text-primary-xiketic text-sm'}>Campaigns</span>
            <div className={'flex flex-row gap-2'}>
              <DateRangePicker filters={filters} onChange={onDateRangeChange} />
              <div className={'flex items-center gap-3'}>
                {view === 'grid' && (
                  <div onClick={() => setView(view === 'grid' ? 'table' : 'grid')}>
                    <IconChangeViewList
                      className={
                        'cursor-pointer stroke-primary-xiketic fill-primary-xiketic'
                      }
                    />
                  </div>
                )}
                {view === 'table' && (
                  <div onClick={() => setView(view === 'table' ? 'grid' : 'table')}>
                    <IconChangeViewCard
                      className={'cursor-pointer stroke-primary-xiketic'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {view === 'table' && (
            <Table
              hidePagination={true}
              emptyLabel={'No campaigns found'}
              columns={campaignColumns}
              filters={{ ...filters.dateRange }}
              fetchFn={useGetSelfServiceCampaignsByDealershipId}
            />
          )}
          {view === 'grid' && (
            <Grid
              emptyLabel={'No campaigns found'}
              fetchFn={useGetSelfServiceCampaignsByDealershipId}
              gridComponent={CampaignOverviewCard.SelfService}
              filters={{ ...filters.dateRange }}
              gridItemProps={{
                isSelfService: true,
              }}
            />
          )}
        </div>
      </div>
      <Outlet />
    </>
  )
}
